
import React, { useState, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from 'sonner';
import BusinessProgressService, { BusinessProgress } from '@/services/BusinessProgressService';
import { useAuth } from '@/contexts/AuthContext';

interface BusinessFormProps {
  business?: BusinessProgress;
  onSave: () => void;
  onCancel: () => void;
}

const BusinessForm: React.FC<BusinessFormProps> = ({ business, onSave, onCancel }) => {
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    business_name: '',
    development_stage: 'development' as 'development' | 'launch' | 'growth',
    description: '',
    industry_type: '', // Changed from industry to industry_type
    funding_required: '0',
    user_id: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (business) {
      setFormData({
        business_name: business.business_name || '',
        development_stage: business.development_stage || 'development',
        description: business.description || '',
        industry_type: business.industry_type || '', // Changed from industry to industry_type
        funding_required: '0',
        user_id: business.user_id || ''
      });
    } else if (user) {
      setFormData(prev => ({
        ...prev,
        user_id: user.id
      }));
    }
  }, [business, user]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name: string, value: string) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.business_name) {
      toast.error('Business name is required');
      return;
    }

    try {
      setIsSubmitting(true);
      console.log('Submitting business data:', formData);

      if (business) {
        // Update existing business
        await BusinessProgressService.updateBusinessProgress(business.id, {
          business_name: formData.business_name,
          development_stage: formData.development_stage,
          description: formData.description,
          industry_type: formData.industry_type || null, // Changed from industry to industry_type
          user_id: formData.user_id
        });
        toast.success('Business updated successfully');
      } else {
        // Create new business
        await BusinessProgressService.createBusinessProgress({
          business_name: formData.business_name,
          development_stage: formData.development_stage,
          description: formData.description,
          industry_type: formData.industry_type || null, // Changed from industry to industry_type
          user_id: formData.user_id
        });
        toast.success('Business created successfully');
      }
      onSave();
    } catch (error) {
      console.error('Error saving business:', error);
      toast.error('Failed to save business');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid gap-2">
        <Label htmlFor="business_name">Business Name *</Label>
        <Input
          id="business_name"
          name="business_name"
          value={formData.business_name}
          onChange={handleChange}
          placeholder="Enter business name"
          required
        />
      </div>

      <div className="grid gap-2">
        <Label htmlFor="development_stage">Development Stage</Label>
        <Select
          value={formData.development_stage}
          onValueChange={(value) => handleSelectChange('development_stage', value)}
        >
          <SelectTrigger>
            <SelectValue placeholder="Select development stage" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="development">Development</SelectItem>
            <SelectItem value="launch">Launch</SelectItem>
            <SelectItem value="growth">Growth</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="grid gap-2">
        <Label htmlFor="industry_type">Industry</Label>
        <Input
          id="industry_type"
          name="industry_type"
          value={formData.industry_type}
          onChange={handleChange}
          placeholder="E.g. Technology, Healthcare, Finance"
        />
      </div>

      <div className="grid gap-2">
        <Label htmlFor="description">Description</Label>
        <Textarea
          id="description"
          name="description"
          value={formData.description || ''}
          onChange={handleChange}
          placeholder="Describe the business"
          rows={4}
        />
      </div>

      <div className="flex justify-end space-x-2 pt-4">
        <Button type="button" variant="outline" onClick={onCancel} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button type="submit" disabled={isSubmitting}>
          {isSubmitting ? 'Saving...' : business ? 'Update Business' : 'Add Business'}
        </Button>
      </div>
    </form>
  );
};

export default BusinessForm;
